import { useState, useEffect } from 'react';
import { Modal, Dropdown, FormItem } from '@getsynapse/design-system';
import { useLanguage } from './LanguageContext'; // Import the custom context hook for language switching
import intl from 'react-intl-universal'; // Import the react-intl-universal library for internationalization
// Define the type for language options
type LanguageOption = {
  label: string;
  value: string;
};

// Define the language options that the user can select from
const languageOptions: LanguageOption[] = [
  { label: 'English', value: 'en' },
  { label: 'French (Canada)', value: 'fr' },
];

// Props for LanguageModal
interface LanguageModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const LanguageModal = ({ isOpen, onClose }: LanguageModalProps) => {
  const { currentLocale, switchLanguage } = useLanguage();

  const [selectedLanguage, setSelectedLanguage] =
    useState<LanguageOption | null>(() => {
      return (
        languageOptions.find((option) => option.value === currentLocale) || null
      );
    });

  useEffect(() => {
    if (isOpen) {
      const currentLanguage = languageOptions.find(
        (option) => option.value === currentLocale
      );
      setSelectedLanguage(currentLanguage || null);
    }
  }, [isOpen, currentLocale]);

  const onLanguageChange = (option: LanguageOption | LanguageOption[]) => {
    const selectedOption = Array.isArray(option) ? option[0] : option;
    setSelectedLanguage(selectedOption);
  };

  // Confirm the selected language and apply it using switchLanguage from the context
  const confirmLanguageSelection = () => {
    if (selectedLanguage) {
      switchLanguage(selectedLanguage.value); // Update the language in the context
    }
    onClose(); // Call the onClose function passed from the parent component to close the modal
  };

  return (
    <Modal
      aria-label='Select Language'
      isOpen={isOpen}
      closeModal={onClose} // Use the onClose function from props to close the modal
      title={intl.get('LANGUAGE_MODAL.TITLE')}
      size='small'
      overlayClassName='z-50'
      actionButtons={[
        {
          children: intl.get('LANGUAGE_MODAL.CONFIRM'),
          onClick: confirmLanguageSelection,
          variant: 'primary',
          disabled: !selectedLanguage, // Disable Confirm button if no language is selected
        },
        {
          children: intl.get('LANGUAGE_MODAL.CANCEL'),
          onClick: onClose, // Close the modal when Cancel is clicked
          variant: 'tertiary',
        },
      ]}
    >
      {/* <Typography>Select your preferred language</Typography> */}
      <FormItem>
        <Dropdown
          options={languageOptions}
          values={selectedLanguage ? [selectedLanguage] : []}
          onChange={onLanguageChange}
          triggerProps={{
            placeholder: selectedLanguage
              ? selectedLanguage.label
              : intl.get('LANGUAGE_MODAL.PLACEHOLDER'),
          }}
          listProps={{ 'data-cy': 'language-options' }}
        />
      </FormItem>
    </Modal>
  );
};

export default LanguageModal;
